import React, { RefObject, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import ChildWrapper, { ChildWrapperRef } from './ChildrenWrapper';
import ConnectorWrapper from 'src/presentation/components/pages/sidekick/threads/components/Connectors/ConnectorWrapper';
import {
  ConnectorsProviderContext,
  RegisteredElement,
  RegisteredElementType,
} from 'src/presentation/components/pages/sidekick/threads/components/Connectors/ConnectorsProvider';
import {
  selectCurrentModule,
  selectCurrentModulesState,
  selectCurrentThread,
} from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import { StyledContainerBorder } from 'src/presentation/components/pages/sidekick/threads/SidekickLayout';
import { SidekickThreadEditorContext } from 'src/presentation/components/pages/sidekick/threads/SidekickThreadEditorContext';
import useOpenThread from 'src/presentation/components/pages/sidekick/threads/hooks/useOpenThread';
import { FixedContainerRef } from '../../SidekickThreadEditor';
import { getThreadModulesHierarchy } from '../../actions/moduleHelper';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import sidekickFastThreadsResource from 'src/actions/sidekick/sidekickFastThreadsResource';

type ChildrenNodesProps = {
  fixedContainerRef: RefObject<FixedContainerRef>;
  moduleContainerRef: RefObject<HTMLDivElement>;
  childrenNodesRef: RefObject<ChildWrapperRef>;
};

const ChildrenNodes = ({ childrenNodesRef, fixedContainerRef, moduleContainerRef }: ChildrenNodesProps) => {
  const { elementsRef } = useContext(ConnectorsProviderContext);

  const [elements, setElements] = useState<Record<string, RegisteredElement> | null>(null);

  const currentModule = useSelector(selectCurrentModule);

  useEffect(() => {
    if (!elementsRef.current) {
      return;
    }
    setElements(elementsRef.current);
  }, [elementsRef]);

  if (!elements || !Object.keys(elements).length) {
    return null;
  }

  return (
    <>
      {Object.keys(elements).map((key: string) => {
        const element = elements[key];

        if (element.type !== 'MODULE') {
          return null;
        }

        if (!element.element) {
          return null;
        }

        const isSelected = currentModule?.id === element?.id;

        if (!isSelected) {
          return null;
        }

        return (
          <ChildWrapper
            ref={childrenNodesRef}
            element={element.element}
            fixedContainerRef={fixedContainerRef}
            isSelected={isSelected}
            key={element.id}
            moduleContainerRef={moduleContainerRef}
            moduleId={element.id}
          >
            <ThreadsList moduleId={element.id} />
          </ChildWrapper>
        );
      })}
    </>
  );
};

const ThreadsList = ({ moduleId }: { moduleId: string }) => {
  const { root } = useContext(SidekickThreadEditorContext);
  const { handleOpenThread } = useOpenThread({ root });

  const { values: threads } = useResourceValues(sidekickFastThreadsResource);
  const currentThread = useSelector(selectCurrentThread);

  const hierarchy = useMemo(() => {
    const threadsHierarchy = getThreadModulesHierarchy(currentThread, threads);
    if (!threadsHierarchy || !Object.keys(threadsHierarchy).length) {
      return [];
    }

    return threadsHierarchy[moduleId] as any;
  }, [currentThread, threads, moduleId]);

  return (
    <>
      {hierarchy?.map(relativeThread => (
        <ThreadNode
          id={`${moduleId}-child-thread-${relativeThread.id}-${relativeThread.modifierId}`}
          key={relativeThread.id}
          moduleId={moduleId}
          onClick={handleOpenThread}
          thread={relativeThread.thread}
          modifierId={relativeThread.modifierId}
        />
      ))}
    </>
  );
};

type ThreadNodeProps = {
  id: string;
  isSourceNode?: boolean;
  onClick: (threadId: string) => void;
  thread: { id: number; name: string };
  moduleId: string;
  modifierId: string;
};

const ThreadNode = ({ id, thread, onClick, moduleId, modifierId }: ThreadNodeProps) => {
  const [isRegistered, setIsRegistered] = useState(false);
  const [type, setType] = useState<RegisteredElementType>('MODIFIER');

  const { registerLine } = useContext(ConnectorsProviderContext);

  const modulesStates = useSelector(selectCurrentModulesState);

  const sType = useMemo(() => (modulesStates[moduleId]?.isOpen ? 'MODIFIER' : 'MODULE'), [modulesStates, moduleId]);
  const lineId = useMemo(() => `${id}-${moduleId}-${modifierId}`, [id, modifierId, moduleId]);

  useEffect(() => {
    if (sType !== type) {
      setType(sType);
      setIsRegistered(false);
    }
  }, [modulesStates, moduleId, setType, type, setIsRegistered, lineId, sType]);

  const handleClick = () => {
    onClick(String(thread.id));
  };

  useEffect(() => {
    if (isRegistered) {
      return;
    }
    // const moduleOrModifierId = sType === 'MODULE' ? moduleId : modifierId;
    const moduleOrModifierId = modifierId; // sType === 'MODULE' ? moduleId : modifierId;
    registerLine(lineId, { source: { id: moduleOrModifierId }, destination: { id } });

    setIsRegistered(true);
  }, [id, lineId, isRegistered, modifierId, moduleId, registerLine, sType]);

  if (!thread) {
    return null;
  }

  return (
    <ConnectorWrapper connectedPointPosition="left" id={id} type="THREAD" meta={null}>
      <StyledItemContainer role="button" className="thread-reference" onClick={handleClick}>
        {thread.name}
      </StyledItemContainer>
    </ConnectorWrapper>
  );
};

const StyledItemContainer = styled.div`
  ${StyledContainerBorder}
  cursor: pointer;
  display: inline-block;
  font-weight: 600;
  padding: 12px 16px;
  text-align: center;

  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-wrap: nowrap;
`;

export default ChildrenNodes;
